import React, { useContext, useEffect, useState, Dispatch, useCallback, useMemo, useRef } from 'react';

import { AuthCheckText } from '@app/components';
import { useToken } from '@app/hooks';

import { useBalance } from '@app/hooks/user/mybalance.tsx';
import TPSLEDIT from '@app/organisms/Trade/TradeDetail/TPSLEDIT.tsx';
import { IconShare2 } from '@tabler/icons-react';
import { Button, message, Modal, Table, Tooltip } from 'antd';
import BigNumber from 'bignumber.js';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { View } from '../../../../../styles/reactnative';
import { ThemeContext } from '../../../../../theme/ThemeProvider';
import NoData from '../../../../components/Common/CFlatList/NoData';
import CRenderEmpty from '../../../../components/CRenderEmpty';
import KrwPrice from '../../../../components/KrwPrice';
import { COLORS } from '../../../../constants';
import { SETTING_TYPE_SYSTEM } from '../../../../constants/SETTING';
import { useExchangeStore } from '../../../../hooks/trade/exchangeStore';
import { useIndexPrice } from '../../../../hooks/trade/indexprice';
import { useTradeModalStore } from '../../../../hooks/trade/modalStore';
import { useTradePosition } from '../../../../hooks/trade/tradePosition';
import useTradeStore from '../../../../hooks/trade/tradeStore';
import { getSetting, useSetting } from '../../../../hooks/user/mysetting';
import ShareModal from '../../../ShareModal';
import useCloseAllStore from '../closeAllStore';
import ContractsColumn from '../Columns/ContractsColumn';
import Closedby from './closedBy';
import { TradeDetailListSection, TradeText } from './styles';
import AddMargin from './AddMargin';
import API from '@/utils/api';
import throttle from 'lodash/throttle';
import useGetCoinTier from '@app/hooks/trade/coinTier';
import { formatKNumber } from '@utils/formatKNumber.tsx';
import { formatDecimal4Truncate } from '@/utils/formatDecimal';
import { pnlCalculate } from '@utils/pnlCalculate.tsx';
import { useMultiMarkPriceStore } from '@app/hooks/multiMarkPriceStore.tsx';
import TradeMarginRatio from './TradeMarginRatio';
import EmptyMessage from '@/app/components/EmptyMessage';
import useScrollLayout from '../useScrollLayout';

interface TradePositionsTableProps {
  item: any;
  amount: any;
  setAmount: Dispatch<any>;
  onClose?: () => void;
}

const TradePositionsTable = ({ parentHeight = 300, type = '', orderBookValue }:any) => {
  const { data: token } = useToken();
  const { textw } = useContext(ThemeContext).theme.COLORS;
  const { t } = useTranslation();

  const [shareData, setShareData] = React.useState<any>(null);

  const [marginModal, setMarginModal] = React.useState<any>(null);
  const [reduceMode, setReduceMode] = React.useState(false);
  const [maxReduceAmount, setMaxReduceAmount] = React.useState(0);

  const [closeAll, setCloseAll] = React.useState(false);

  const closed = useCloseAllStore((state) => state.closed);
  const toggleClosed = useCloseAllStore((state) => state.toggleClosed);

  const [refresh, setRefresh] = React.useState(false);
  const [forceUpdate, setForceUpdate] = React.useState(0);

  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const intervalId = setInterval(() => {
      setForceUpdate(prev => prev + 1);
    }, 1000);

    return () => clearInterval(intervalId);
  }, []);

  const { mutate: mutateModalStore, data: modalStore } = useTradeModalStore();

  const { data: tradeStore } = useTradeStore();
  const orderType = tradeStore?.main_sub || "M";

  const { data: setting, updateSetting } = useSetting(SETTING_TYPE_SYSTEM);
  const equivshow = getSetting(setting, 'EQUIVSHO', 1) == 1 ? true : false;

  const { getPrice } = useIndexPrice(tradeStore?.ep_id || 0);
  const { EPs } = useExchangeStore();
  const { data: pos, mutate } = useTradePosition();

  const currency = tradeStore?.pairs?.split('/')[1];
  const { data: balance } = useBalance(currency, 1);

  const [amount, setAmount] = useState<any>(0);

  const open_pos = pos || [];

  let pnl = 0;
  for (let i = 0; i < open_pos?.length; i++) {
    if (open_pos[i].cross_iso == 'I') continue;

    if (open_pos[i].long_short === 'L') {
      pnl +=
        (getPrice(open_pos?.[i]?.exchange_pairs_id) -
          open_pos?.[i]?.open_price) *
        open_pos[i]?.amount *
        open_pos[i]?.leverage;
    } else {
      pnl +=
        (open_pos[i]?.open_price - getPrice(open_pos?.[i]?.exchange_pairs_id)) *
        open_pos[i]?.amount *
        open_pos[i]?.leverage;
    }
  }

  let list = pos;

  if (modalStore?.iscurrent) {
    list = pos?.filter(
      (item: any) => item?.exchange_pairs_id == tradeStore?.ep_id
    );
  } else {
    list = pos ? [...pos].sort((a, b) => {
      const isA = a?.exchange_pairs_id == tradeStore?.ep_id ? 1 : 0;
      const isB = b?.exchange_pairs_id == tradeStore?.ep_id ? 1 : 0;

      if (isA !== isB) return isB - isA;

      const aValue = BigNumber(a?.amount)
        .multipliedBy(getPrice(a?.exchange_pairs_id))
        .multipliedBy(a?.leverage)
        .toNumber();

      const bValue = BigNumber(b?.amount)
        .multipliedBy(getPrice(b?.exchange_pairs_id))
        .multipliedBy(b?.leverage)
        .toNumber();

      return bValue - aValue; // 내림차순
    }) : [];
  }

  const { needsScroll, headerHeight } = useScrollLayout(containerRef, list, parentHeight);

  useEffect(() => {
    setRefresh(true);
    setTimeout(() => {
      setRefresh(false);
    }, 100);
  }, [list?.length]);

  const throttledGetPrice = throttle((id: number) => {
    return getPrice(id)
  }, 1000)

  // 정렬에 사용할 유틸 함수들
  const getEp = (id: any) => EPs?.find((ep: any) => +ep.id === +id);
  const getPairs = (item: any) => getEp(item?.exchange_pairs_id)?.pairs || '';
  const getSymbolLower = (item: any) => getPairs(item).replace('/', '').toLowerCase();
  const currentPriceOf = (item: any) => Number(getPrice(item?.exchange_pairs_id) || 0);

  const qtyValueOf = (item: any) => {
    const curr = currentPriceOf(item);
    if (orderType === 'M')
      return BigNumber(item?.amount).multipliedBy(item?.leverage).toNumber();
    return BigNumber(item?.amount)
      .multipliedBy(curr)
      .multipliedBy(item?.leverage)
      .toNumber();
  };

  const entryPriceOf = (item: any) => Number(item?.price) || 0;
  const liqPriceOf = (item: any) => Number(item?.liquid_price) || 0;

  
  const { getCoinTier } = useGetCoinTier();

  const getUnrealizedPnl = (item: any, currentPrice: number, amountWithLev: number) => {
    if (item?.long_short === 'L') {
      return BigNumber(currentPrice).minus(item?.price).multipliedBy(amountWithLev).toNumber();
    } else {
      return BigNumber(item?.price).minus(currentPrice).multipliedBy(amountWithLev).toNumber();
    }
  };

  const getTotalDeposit = (openPositions: any[], getPriceFn: (id: number) => number, walletBalance: any) => {
    let pnl = 0;
    for (let i = 0; i < openPositions?.length; i++) {
      if (openPositions[i].cross_iso == 'I') continue;
      if (openPositions[i].long_short === 'L') {
        pnl +=
          (getPriceFn(openPositions?.[i]?.exchange_pairs_id) - openPositions?.[i]?.open_price) *
          openPositions[i]?.amount *
          openPositions[i]?.leverage;
      } else {
        pnl +=
          (openPositions[i]?.open_price - getPriceFn(openPositions?.[i]?.exchange_pairs_id)) *
          openPositions[i]?.amount *
          openPositions[i]?.leverage;
      }
    }
    const base = BigNumber(walletBalance?.balance).plus(pnl).toNumber();
    return isNaN(base) ? 0 : base;
  };

  const marginRatioOf = (item: any) => {
    const isCross = item?.cross_iso === 'C';
    if (isCross) {
      // 전체 유지증거금
      const sumMm = (pos || [])?.reduce((acc: number, curr: any) => {
        const ep = getEp(curr?.exchange_pairs_id);
        const currentPrice = currentPriceOf(curr);
        const coin = ep?.pairs?.split('/')[0];
        const amountUsdt = Number(curr?.amount) * Number(curr?.leverage) * currentPrice;
        const { mmr, ma } = getCoinTier(coin, amountUsdt);
        const maintenanceMargin = Number(curr?.amount) * Number(curr?.leverage) * Number(curr?.price) * mmr - ma;
        return acc + (maintenanceMargin || 0);
      }, 0);
      const totalDeposit = getTotalDeposit(pos || [], getPrice, balance);
      return (sumMm / (totalDeposit || 1)) * 100;
    } else {
      const ep = getEp(item?.exchange_pairs_id);
      const currentPrice = currentPriceOf(item);
      const coin = ep?.pairs?.split('/')[0];
      const amountUsdt = Number(item?.amount) * Number(item?.leverage) * currentPrice;
      const { mmr, ma } = getCoinTier(coin, amountUsdt);
      const maintenanceMargin = Number(item?.amount) * Number(item?.leverage) * Number(item?.price) * mmr - ma;
      const amountWithLev = Number(item?.amount) * Number(item?.leverage);
      const unrealized = getUnrealizedPnl(item, currentPrice, amountWithLev);
      const marginBalance = Number(item?.margin || 0) + unrealized;
      return (maintenanceMargin / (marginBalance || 1)) * 100;
    }
  };

  const unrealizedPnlOf = (item: any) => {
    const currentPrice = currentPriceOf(item);
    const amountWithLev = Number(item?.amount) * Number(item?.leverage);
    let pnl = 0;
    if (item?.long_short === 'L') {
      pnl = BigNumber(currentPrice).minus(item?.price).multipliedBy(amountWithLev).toNumber();
    } else {
      pnl = BigNumber(item?.price).minus(currentPrice).multipliedBy(amountWithLev).toNumber();
    }
    return pnl;
  };

  const realizedPnlWithFeesOf = (item: any) => {
    const entryFee = item?.orders?.[0]?.fee_price || 0;
    let exitFees = 0;
    if (item?.orders && item.orders.length > 1) {
      for (let i = 1; i < item.orders.length; i++) {
        if (item.orders?.[i]?.fee_price) {
          exitFees += Number(item.orders?.[i].fee_price);
        }
      }
    }
    const originalAmount = item?.orders?.[0]?.amount || 0;
    const closedAmount = originalAmount - (item?.amount || 0);
    const closedRatio = originalAmount > 0 ? BigNumber(closedAmount).dividedBy(originalAmount).toNumber() : 0;
    const realizedEntryFee = BigNumber(entryFee).multipliedBy(closedRatio).toNumber();
    let realizedPnLWithFees = 0;
    if (closedAmount > 0 && item.pnl_price) {
      realizedPnLWithFees = BigNumber(item.pnl_price).minus(realizedEntryFee).minus(exitFees).toNumber();
    } else if (closedAmount === 0 && entryFee > 0) {
      realizedPnLWithFees = -Number(entryFee);
    }
    return realizedPnLWithFees;
  };

  const handleOk = () => {
    if (amount == 0) {
      message.error(t('pleaseEnterTheMargin'));
      return;
    }

    // 감소 모드일 때는 음수값만 허용
    if (amount > 0 && reduceMode) {
      message.error(t('reduceModeMustBeNegative'));
      return;
    }

    // 증가 모드일 때는 양수값만 허용
    if (amount < 0 && !reduceMode) {
      message.error(t('addModeMustBePositive'));
      return;
    }

    // 감소 모드에서 최대 감소 가능 금액 초과 여부 확인
    if (reduceMode && Math.abs(amount) > maxReduceAmount) {
      message.error(t('exceededMaxReduceAmount'));
      return;
    }

    API.postNoLogout('/trade/margin', token, {
      position: marginModal?.id,
      amount: amount,
    })
      .then((res) => {
        if (res?.result?.success) {
          if (amount >= 0) {
            message.success(t('marginAdded'));
          } else {
            message.success(t('marginReduced'));
          }
          try {
            setTimeout(() => {
              mutate();
            }, 100);
          } catch (e) {
            console.log('POS', e);
          }
          setMarginModal(false);
        } else {
          if (res?.result?.message) {
            message.error(res?.result?.message);
          } else {
            message.error(t('failedToUpdateMargin'));
          }
        }
      })
      .catch((e) => {
        console.error('marginadd', e);
        message.error(t('failedToAddMargin'));
      });
  };

  const handleReduceModeChange = useCallback((isReduce) => {
    setReduceMode(isReduce);
    // 모드 전환시 입력값 초기화
    setAmount('');
  }, []);
  
  const handleMaxReduceAmountChange = useCallback((amount) => {
    setMaxReduceAmount(amount);
  }, []);

  const { connect } = useMultiMarkPriceStore();

  useEffect(() => {
    if (!EPs?.length || !list?.length) return;

    const symbols = list
      .map((item) => {
        const ep = EPs.find((ep) => +ep.id === +item.exchange_pairs_id);
        return ep?.pairs?.replace("/", "").toLowerCase();
      })
      .filter(Boolean);

    if (symbols.length) {
      connect([...new Set(symbols)] as string[]);
    }
  }, [list]);
  
  return (
    <TradeDetailListSection type={type} ref={containerRef}>
      <Modal
        footer={[
          <Button key="submit" type="primary" onClick={handleOk}>
            {t("confirm")}
          </Button>,
        ]}
        centered
        onCancel={() => {
          setMarginModal(false);
        }}
        title={t('updateMargin')}
        open={marginModal ? true : false}
      >
        <View>
          <AddMargin 
            item={marginModal} 
            amount={amount} 
            setAmount={setAmount} 
            onReduceModeChange={handleReduceModeChange}
            onMaxReduceAmountChange={handleMaxReduceAmountChange}
          />
        </View>
      </Modal>

      {list?.length ? (
        <StyledTableContainer>
          <Table
            showHeader={true}
            bordered={false}
            loading={refresh}
            dataSource={refresh ? [] : list}
            columns={
              type === 'orders'
                ? [
                  {
                    title: t('contracts') + '',
                    dataIndex: 'contracts',
                    align: 'center',
                    key: 'contracts',
                    width: 100,
                    sorter: (a: any, b: any) => {
                      const aPairs = getPairs(a);
                      const bPairs = getPairs(b);
                      return (aPairs || '').localeCompare(bPairs || '');
                    },
                    render: (text: any, item) => (
                      <div style={{ width: '100%', display: 'flex', justifyContent: 'flex-end' }}>
                        <ContractsColumn item={item} />
                      </div>
                    ),
                  },
                  {
                    title: t('qty') + '',
                    dataIndex: 'amount',
                    key: 'amount',
                    align: 'right',
                    sorter: (a: any, b: any) => qtyValueOf(a) - qtyValueOf(b),
                    render: (text: any, item: any) => {
                      const EP = EPs?.find(
                        (item2: any) => +item2.id == +item?.exchange_pairs_id
                      );
                      const coin = EP?.pairs?.split('/')[0];
                      
                      return (
                        <TradeText
                          style={{
                            letterSpacing: 0,
                            minWidth: 150,
                            whiteSpace: 'nowrap',
                            lineHeight: 'normal',
                            flexDirection: 'column',
                            alignContent: 'center',
                            alignItems: 'flex-end',
                            fontWeight: 'bold',
                          }}
                        >
                          {orderType === 'M' ? BigNumber(item?.amount)
                            .multipliedBy(item?.leverage)
                            .toFormat(EP?.dp || EP?.dp2 || 3) : BigNumber(item?.amount)
                              .multipliedBy(getPrice(item?.exchange_pairs_id))
                              .multipliedBy(item?.leverage)
                              .toFormat(4)}{' '}
                          {orderType === 'M' ? coin : 'USDT'}
                        </TradeText>
                      );
                    },
                  },
                  {
                    title: t('avgEntryPrice') + '',
                    dataIndex: 'open_price',
                    key: 'open_price',
                    align: 'right',
                    sorter: (a: any, b: any) => entryPriceOf(a) - entryPriceOf(b),
                    render: (text: any, item) => {
                      const EP = EPs?.find(
                        (item2: any) => +item2.id == +item?.exchange_pairs_id
                      );

                      return (
                        <TradeText
                          style={{
                            letterSpacing: 0,
                            minWidth: 90,
                            lineHeight: 1,
                            flexDirection: 'column',
                            alignContent: 'center',
                            alignItems: 'flex-end',
                            justifyContent: 'flex-end',
                          }}
                        >
                          {BigNumber(item?.price).toFormat(EP?.dp || 3)}
                        </TradeText>
                      );
                    },
                  },
                  {
                    title: t('markPrice') + '',
                    dataIndex: 'mark_price',
                    key: 'mark_price',
                    align: 'right',
                    sorter: (a: any, b: any) => currentPriceOf(a) - currentPriceOf(b),
                    render: (text: any, item) => {
                      const EP = EPs?.find(
                        (item2: any) => +item2.id == +item?.exchange_pairs_id
                      );
                      return (
                        <TradeText
                          style={{
                            letterSpacing: 0,
                            minWidth: 90,
                            lineHeight: 1,
                            flexDirection: 'column',
                            alignContent: 'center',
                            alignItems: 'flex-end',
                            justifyContent: 'flex-end',
                          }}
                        >
                          {BigNumber(
                            throttledGetPrice(item?.exchange_pairs_id)
                          ).toFormat(EP?.dp || 3)}
                        </TradeText>
                      );
                    },
                  },
                  {
                    title: t('liqPrice') + '',
                    dataIndex: 'liquidation_price',
                    key: 'liquidation_price',
                    align: 'right',
                    width: 150,
                    sorter: (a: any, b: any) => liqPriceOf(a) - liqPriceOf(b),
                    render: (text: any, item) => {
                      const EP = EPs?.find(
                        (item2: any) => +item2.id == +item?.exchange_pairs_id
                      );
                      const liquidPrice = BigNumber(item?.liquid_price);
                      const displayValue = liquidPrice.isNegative() ? '-' : liquidPrice.toFormat(EP?.dp || 3);
                      
                      if (liquidPrice.isNegative()) {
                        return (
                          <Tooltip title={t('positionRiskLowTooltip')}>
                            <TradeText
                              style={{
                                letterSpacing: 0,
                                minWidth: 90,
                                lineHeight: 1,
                                flexDirection: 'column',
                                alignContent: 'center',
                                alignItems: 'flex-end',
                                justifyContent: 'flex-end',
                                cursor: 'help',
                              }}
                            >
                              {displayValue}
                            </TradeText>
                          </Tooltip>
                        );
                      }
                      
                      return (
                        <TradeText
                          style={{
                            letterSpacing: 0,
                            minWidth: 90,
                            lineHeight: 1,
                            flexDirection: 'column',
                            alignContent: 'center',
                            alignItems: 'flex-end',
                          }}
                        >
                          {displayValue}
                        </TradeText>
                      );
                    },
                  },
                  {
                    title: t('margin') + '',
                    dataIndex: 'margin',
                    key: 'margin',
                    align: 'right',
                    width: 150,
                    sorter: (a: any, b: any) => Number(a?.margin || 0) - Number(b?.margin || 0),
                    render: (text: any, item, data) => {
                      const EP = EPs?.find(
                        (item2: any) => +item2.id == +item?.exchange_pairs_id
                      );
                      const currency = EP?.pairs?.split('/')[1];
                      return (
                        <TradeText
                          style={{
                            letterSpacing: 0,
                            minWidth: 90,
                            lineHeight: 1,
                            flexDirection: 'column',
                            alignContent: 'center',
                            alignItems: 'flex-end',
                            justifyContent: 'flex-end',
                          }}
                        >
                          <div
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: 12,
                            }}
                          >
                            <KrwPrice
                              price={item?.margin}
                              currency={currency}
                              color={false}
                              equiv={true}
                              dp={3}
                            />
                            {item?.cross_iso === 'I' && (
                              <div
                                style={{
                                  fontSize: 20,
                                  marginBottom: 6,
                                  color: '#F87943',
                                  cursor: 'pointer',
                                }}
                              >
                                +
                              </div>
                            )}
                          </div>
                        </TradeText>
                      );
                    },
                  },
                  {
                    title: t('margin') + ' ' + t('ratio'),
                    dataIndex: 'margin_ratio',
                    key: 'margin_ratio',
                    align: 'right',
                    sorter: (a: any, b: any) => marginRatioOf(a) - marginRatioOf(b),
                    render: (text: any, item) => {
                      const EP = EPs?.find(
                        (item2: any) => +item2.id == +item?.exchange_pairs_id
                      );
                      const currency = EP?.pairs?.split('/')[1];
                      
                      // 현재 가격
                      const currentPrice = getPrice(item?.exchange_pairs_id);
                      
                      // 청산 가격
                      const liquidationPrice = BigNumber(item?.liquid_price).toNumber();
                      
                      // 진입 가격
                      const entryPrice = BigNumber(item?.price).toNumber();
                      
                      // 청산 예상가와 현재 가격 사이의 거리 계산
                      let priceDistance = 0;
                      let priceTotalDistance = 0;
                      
                      if (item?.long_short === 'L') {
                        // 롱 포지션: 진입가 - 청산가 = 총 거리
                        priceTotalDistance = entryPrice - liquidationPrice;
                        // 현재가 - 청산가 = 현재 거리
                        priceDistance = currentPrice - liquidationPrice;
                      } else {
                        // 숏 포지션: 청산가 - 진입가 = 총 거리
                        priceTotalDistance = liquidationPrice - entryPrice;
                        // 청산가 - 현재가 = 현재 거리
                        priceDistance = liquidationPrice - currentPrice;
                      }
                      
                      // 마진 비율 계산: (현재 거리 / 총 거리) * 100
                      // 가격이 청산가에 가까워질수록 비율이 0에 가까워짐
                      let marginRatio = priceTotalDistance > 0 
                        ? (priceDistance / priceTotalDistance) * 100
                        : 0;
                      
                      // 마진 비율 반전 처리: 100에서 빼서 청산가에 가까울수록 100%에 가까워지게 함
                      marginRatio = 100 - marginRatio;
                      
                      // 0보다 작으면 0으로, 100보다 크면 100으로 제한
                      marginRatio = Math.max(0, Math.min(100, marginRatio));
                      
                      return (
                        <TradeText
                          style={{
                            letterSpacing: 0,
                            minWidth: 90,
                            lineHeight: 1,
                            flexDirection: 'column',
                            alignItems: 'flex-end',
                            justifyContent: 'flex-end',
                            color: marginRatio > 80 ? COLORS.lightRed : marginRatio > 50 ? '#f87943' : 'inherit'
                          }}
                        >
                          {marginRatio.toFixed(2)}%
                        </TradeText>
                      );
                    },
                  },
                  {
                    title: t('unrealizedplpercent') + '',
                    dataIndex: 'unrealized_pl_percent',
                    key: 'unrealized_pl_percent',
                    width: 180,
                    sorter: (a: any, b: any) => unrealizedPnlOf(a) - unrealizedPnlOf(b),
                    onHeaderCell: (column) => ({
                      style: {
                        textAlign: 'right',
                        paddingRight: '32px',
                      },
                    }),
                    render: (text: any, item) => {
                      const EP = EPs?.find(
                        (item2: any) => +item2.id == +item?.exchange_pairs_id
                      );
                      const currency = EP?.pairs?.split('/')[1];
                      let pnl = 0;
                      const currentPrice = getPrice(item?.exchange_pairs_id);
                      const amount = item?.amount * item?.leverage


                      if (item?.long_short === 'L') {
                        pnl = BigNumber(currentPrice)
                          .minus(item?.price) // 진입가
                          .multipliedBy(amount)
                          .toNumber();
                      } else {
                        pnl = BigNumber(item?.price)
                          .minus(currentPrice)
                          .multipliedBy(amount)
                          .toNumber();
                      }

                      // 진입 수수료 계산 - 전체 포지션에 대한 수수료
                      const entryFee = item?.orders?.[0]?.fee_price || 0;
                      
                      // 실현된 청산 수수료 계산
                      let realizedExitFees = 0;
                      if (item?.orders && item.orders.length > 1) {
                        for (let i = 1; i < item.orders.length; i++) {
                          if (item.orders[i]?.fee_price) {
                            realizedExitFees += Number(item.orders[i].fee_price);
                          }
                        }
                      }

                      // 남은 포지션 비율 계산
                      const originalAmount = item?.orders?.[0]?.amount || item?.amount;
                      const remainingRatio = BigNumber(item.amount).dividedBy(originalAmount).toNumber();
                      
                      // 남은 진입 수수료 계산 (전체 진입 수수료 * 남은 비율)
                      const remainingEntryFee = BigNumber(entryFee)
                        .multipliedBy(remainingRatio)
                        .toNumber();
                      
                      // 예상 청산 수수료 계산 (남은 진입 수수료와 동일하게 가정)
                      const estimatedExitFee = remainingEntryFee;

                      // 최종 PnL 계산: 미실현 손익 - 남은 진입 수수료 - 예상 청산 수수료
                      const pnlWithFees = BigNumber(pnl)
                        .minus(remainingEntryFee)
                        .minus(estimatedExitFee)
                        .toNumber();

                      return (
                        <View style={{ flexDirection: 'row', gap: 0, justifyContent: 'flex-end' }}>
                          <KrwPrice
                            price={pnl}
                            equiv={equivshow}
                            ratemargin={formatDecimal4Truncate(item?.margin)}
                            currency={currency}
                            color={true}
                            dp={4}
                          />
                          <CRenderEmpty id={item?.exchange_pairs_id} />

                          <Button
                            style={{ border: 'none', marginBottom: 5 }}
                            ghost
                            size="small"
                            icon={
                              <IconShare2
                                color={
                                  pnl > 0
                                    ? COLORS.lightGreen
                                    : pnl < 0
                                      ? COLORS.lightRed
                                      : textw
                                }
                                size={16}
                              />
                            }
                            onClick={() => {
                              setShareData(null);
                              setTimeout(() => {
                                setShareData(item);
                              }, 100);
                            }}
                          />
                        </View>
                      );
                    },
                  },
                  {
                    title: t('realizedpl') + '',
                    dataIndex: 'realized_pl',
                    key: 'realized_pl',
                    align: 'right',
                    width: 180,
                    sorter: (a: any, b: any) => realizedPnlWithFeesOf(a) - realizedPnlWithFeesOf(b),
                    render: (text: any, item) => {
                      const EP = EPs?.find(
                        (item2: any) => +item2.id == +item?.exchange_pairs_id
                      );
                      const currency = EP?.pairs?.split('/')[1];

                      const entryFee = item?.orders?.[0]?.fee_price || 0;

                      let exitFees = 0;
                      if (item?.orders && item.orders.length > 1) {
                        for (let i = 1; i < item.orders.length; i++) {
                          if (item.orders[i]?.fee_price) {
                            exitFees += Number(item.orders[i].fee_price);
                          }
                        }
                      }
                      
                      // 원래 포지션 대비 청산된 비율 계산
                      const originalAmount = item?.orders?.[0]?.amount || 0;
                      const closedAmount = originalAmount - (item?.amount || 0);
                      const closedRatio = originalAmount > 0 ? 
                        BigNumber(closedAmount).dividedBy(originalAmount).toNumber() : 0;
                      
                      // 실현된 진입 수수료 (원래 진입 수수료 * 청산 비율)
                      const realizedEntryFee = BigNumber(entryFee).multipliedBy(closedRatio).toNumber();

                      const realizedPnLWithFees = BigNumber(item?.pnl_price || 0)
                        .minus(realizedEntryFee)
                        .minus(exitFees)
                        .toNumber();

                      return (
                        <View style={{ display: 'flex',flexDirection: 'row', justifyContent: 'flex-end', width: '100%' }}>
                          <KrwPrice
                            price={formatDecimal4Truncate(realizedPnLWithFees)}
                            currency={currency}
                            equiv={equivshow}
                            dp={4}
                          />
                        </View>
                      );
                    },
                  },
                ]
                : [
                  {
                    width: '9%',
                    title: t('contracts') + '',
                    dataIndex: 'contracts',
                    align: 'left',
                    key: 'contracts',
                    sorter: (a: any, b: any) => {
                      const aPairs = getPairs(a);
                      const bPairs = getPairs(b);
                      return (aPairs || '').localeCompare(bPairs || '');
                    },
                    render: (text: any, item) => (
                      <ContractsColumn item={item} />
                    ),
                  },
                  {
                    width: '9%',
                    title: t('qty') + '',
                    dataIndex: 'amount',
                    key: 'amount',
                    align: 'left',
                    sorter: (a: any, b: any) => qtyValueOf(a) - qtyValueOf(b),
                    render: (text: any, item: any) => {
                      const EP = EPs?.find(
                        (item2: any) => +item2.id == +item?.exchange_pairs_id
                      );
                      const coin = EP?.pairs?.split('/')[0];
                      const currentPrice = getPrice(item?.exchange_pairs_id);
                      return (
                        <TradeText
                          style={{
                            letterSpacing: 0,
                            minWidth: 90,
                            whiteSpace: 'nowrap',
                            lineHeight: 'normal',
                            flexDirection: 'column',
                            alignItems: 'left',
                            fontWeight: 'bold',
                          }}
                        >
                          {orderType === 'M' ? BigNumber(item?.amount)
                            .multipliedBy(item?.leverage)
                            .toFormat(4) : BigNumber(item?.amount)
                              .multipliedBy(currentPrice)
                              .multipliedBy(item?.leverage)
                              .toFormat(4)}{' '}
                          {orderType === 'M' ? coin : 'USDT'}
                        </TradeText>
                      );
                    },
                  },
                  {
                    width: '9%',
                    title: t('entryPrice') + '',
                    dataIndex: 'open_price',
                    key: 'open_price',
                    align: 'left',
                    sorter: (a: any, b: any) => entryPriceOf(a) - entryPriceOf(b),
                    render: (text: any, item) => {
                      const EP = EPs?.find(
                        (item2: any) => +item2.id == +item?.exchange_pairs_id
                      );

                      return (
                        <TradeText
                          style={{
                            letterSpacing: 0,
                            minWidth: 90,
                            lineHeight: 1,
                            flexDirection: 'column',
                            alignItems: 'left',
                            justifyContent: 'center',
                          }}
                        >
                          {BigNumber(item?.price).toFormat(EP?.dp || 3)}
                        </TradeText>
                      );
                    },
                  },
                  {
                    width: '9%',
                    title: t('marketAveragePrice') + '',
                    dataIndex: 'marketAveragePrice',
                    key: 'marketAveragePrice',
                    align: 'left',
                    sorter: (a: any, b: any) => currentPriceOf(a) - currentPriceOf(b),
                    render: (text: any, item) => {
                      const ep = EPs?.find((ep) => +ep.id === +item.exchange_pairs_id);
                      const symbol = ep?.pairs?.replace("/", "").toLowerCase();
                      const price = useMultiMarkPriceStore.getState().prices[symbol] || 0;


                      return (
                        <TradeText
                          style={{
                            letterSpacing: 0,
                            minWidth: 90,
                            lineHeight: 1,
                            flexDirection: 'column',
                            alignItems: 'left',
                          }}
                        >
                          {/*{BigNumber(*/}
                          {/*  getPrice(item?.exchange_pairs_id)*/}
                          {/*).toFormat(EP?.dp || 3)}*/}
                          {BigNumber(price).toFormat(ep?.dp || 3)}
                        </TradeText>
                      );
                    },
                  },
                  {
                    width: '9%',
                    title: t('liqPrice') + '',
                    dataIndex: 'liquidation_price',
                    key: 'liquidation_price',
                    align: 'left',
                    sorter: (a: any, b: any) => liqPriceOf(a) - liqPriceOf(b),
                    render: (text: any, item) => {
                      const EP = EPs?.find(
                        (item2: any) => +item2.id == +item?.exchange_pairs_id
                      );
                      const liquidPrice = BigNumber(item?.liquid_price);
                      const displayValue = liquidPrice.isNegative() ? '--' : liquidPrice.toFormat(EP?.dp || 3);
                      
                      if (liquidPrice.isNegative()) {
                        return (
                          <Tooltip title={t('positionRiskLowTooltip')}>
                            <TradeText
                              style={{
                                letterSpacing: 0,
                                minWidth: 90,
                                lineHeight: 1,
                                flexDirection: 'column',
                                alignItems: 'left',
                                cursor: 'help',
                                color: '#FF5C17'
                              }}
                            >
                              {displayValue}
                            </TradeText>
                          </Tooltip>
                        );
                      }
                      
                      return (
                        <TradeText
                          style={{
                            letterSpacing: 0,
                            minWidth: 90,
                            lineHeight: 1,
                            flexDirection: 'column',
                            alignItems: 'left',
                            color: '#FF5C17'
                          }}
                        >
                          {displayValue}
                        </TradeText>
                      );
                    },
                  },
                  {
                    width: '9%',
                    title: t('margin') + '',
                    dataIndex: 'margin',
                    key: 'margin',
                    align: 'left',
                    sorter: (a: any, b: any) => Number(a?.margin || 0) - Number(b?.margin || 0),
                    render: (text: any, item) => {
                      const EP = EPs?.find(
                        (item2: any) => +item2.id == +item?.exchange_pairs_id
                      );
                      const currency = EP?.pairs?.split('/')[1];
                      return (
                        <TradeText
                          style={{
                            letterSpacing: 0,
                            minWidth: 90,
                            lineHeight: 1,
                            flexDirection: 'column',
                            alignItems: 'left',
                          }}
                        >
                          <div
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: 12,
                            }}
                          >
                            <KrwPrice
                              price={formatDecimal4Truncate(item?.margin)}
                              currency={currency}
                              color={false}
                              equiv={true}
                              dp={4}
                            />
                            {item?.cross_iso === 'I' && (
                              <PlusButton
                                onClick={() => {
                                  setMarginModal(item);
                                }}
                              >
                                +
                              </PlusButton>
                            )}
                          </div>
                        </TradeText>
                      );
                    },
                  },
                  {
                    width: '9%',
                    title: t('margin') + ' ' + t('ratio'),
                    dataIndex: 'margin_ratio',
                    key: 'margin_ratio',
                    align: 'left',
                    sorter: (a: any, b: any) => marginRatioOf(a) - marginRatioOf(b),
                    render: (text: any, item) => {
                      // const EP = EPs?.find(
                      //   (item2: any) => +item2.id == +item?.exchange_pairs_id
                      // );
                      // const currency = EP?.pairs?.split('/')[1];
                      
                      // // 현재 가격
                      // const currentPrice = getPrice(item?.exchange_pairs_id);
                      
                      // // 청산 가격
                      // const liquidationPrice = BigNumber(item?.liquid_price).toNumber();
                      
                      // // 진입 가격
                      // const entryPrice = BigNumber(item?.price).toNumber();
                      
                      // // 청산 예상가와 현재 가격 사이의 거리 계산
                      // let priceDistance = 0;
                      // let priceTotalDistance = 0;
                      
                      // if (item?.long_short === 'L') {
                      //   // 롱 포지션: 진입가 - 청산가 = 총 거리
                      //   priceTotalDistance = entryPrice - liquidationPrice;
                      //   // 현재가 - 청산가 = 현재 거리
                      //   priceDistance = currentPrice - liquidationPrice;
                      // } else {
                      //   // 숏 포지션: 청산가 - 진입가 = 총 거리
                      //   priceTotalDistance = liquidationPrice - entryPrice;
                      //   // 청산가 - 현재가 = 현재 거리
                      //   priceDistance = liquidationPrice - currentPrice;
                      // }
                      
                      // // 마진 비율 계산: (현재 거리 / 총 거리) * 100
                      // // 가격이 청산가에 가까워질수록 비율이 0에 가까워짐
                      // let marginRatio = priceTotalDistance > 0 
                      //   ? (priceDistance / priceTotalDistance) * 100
                      //   : 0;
                      
                      // // 마진 비율 반전 처리: 100에서 빼서 청산가에 가까울수록 100%에 가까워지게 함
                      // marginRatio = 100 - marginRatio;
                      
                      // // 0보다 작으면 0으로, 100보다 크면 100으로 제한
                      // marginRatio = Math.max(0, Math.min(100, marginRatio));
                      
                      return (
                       <TradeMarginRatio item={item} />
                      );
                    },
                  },
                  {
                    width: '9%',
                    title: t('unrealizedplpercent') + '',
                    dataIndex: 'unrealized_pl_percent',
                    key: 'unrealized_pl_percent',
                    align: 'left',
                    sorter: (a: any, b: any) => unrealizedPnlOf(a) - unrealizedPnlOf(b),
                    render: (text: any, item) => {
                      const EP = EPs?.find(
                        (item2: any) => +item2.id == +item?.exchange_pairs_id
                      );
                      const currency = EP?.pairs?.split('/')[1];
                      let pnl = 0;
                      const currentPrice = getPrice(item?.exchange_pairs_id);
                      const amount = item?.amount * item?.leverage

                      if (item?.long_short === 'L') {
                        pnl = BigNumber(currentPrice)
                          .minus(item?.price) // 진입가
                          .multipliedBy(amount)
                          .toNumber();
                      } else {
                        pnl = BigNumber(item?.price)
                          .minus(currentPrice)
                          .multipliedBy(amount)
                          .toNumber();
                      }

                      // 진입 수수료 계산 - 전체 포지션에 대한 수수료
                      const entryFee = item?.orders?.[0]?.fee_price || 0;
                      
                      // 실현된 청산 수수료 계산
                      let realizedExitFees = 0;
                      if (item?.orders && item.orders.length > 1) {
                        for (let i = 1; i < item.orders.length; i++) {
                          if (item.orders[i]?.fee_price) {
                            realizedExitFees += Number(item.orders[i].fee_price);
                          }
                        }
                      }

                      // 남은 포지션 비율 계산
                      const originalAmount = item?.orders?.[0]?.amount || item?.amount;
                      const remainingRatio = BigNumber(item.amount).dividedBy(originalAmount).toNumber();
                      
                      // 남은 진입 수수료 계산 (전체 진입 수수료 * 남은 비율)
                      const remainingEntryFee = BigNumber(entryFee)
                        .multipliedBy(remainingRatio)
                        .toNumber();
                      
                      // 예상 청산 수수료 계산 (남은 진입 수수료와 동일하게 가정)
                      const estimatedExitFee = remainingEntryFee;

                      // 최종 PnL 계산: 미실현 손익 - 남은 진입 수수료 - 예상 청산 수수료
                      const pnlWithFees = BigNumber(pnl)
                        .minus(remainingEntryFee)
                        .minus(estimatedExitFee)
                        .toNumber();

                      return (
                        <View style={{ flexDirection: 'row', gap: 0, justifyContent: 'left' }}>
                          <KrwPrice
                            price={pnl}
                            equiv={equivshow}
                            ratemargin={formatDecimal4Truncate(item?.margin)}
                            currency={currency}
                            color={true}
                            dp={4}
                          />
                          <CRenderEmpty id={item?.exchange_pairs_id} />

                          <Button
                            style={{ border: 'none', marginBottom: 5 }}
                            ghost
                            size="small"
                            icon={
                              <IconShare2
                                color={
                                  pnl > 0
                                    ? COLORS.lightGreen
                                    : pnl < 0
                                      ? COLORS.lightRed
                                      : textw
                                }
                                size={16}
                              />
                            }
                            onClick={() => {
                              setShareData(null);
                              setTimeout(() => {
                                setShareData(item);
                              }, 100);
                            }}
                          />
                        </View>
                      );
                    },
                  },
                  {
                    width: '9%',
                    title: t('realizedpl') + '',
                    dataIndex: 'realized_pl',
                    key: 'realized_pl',
                    align: 'left',
                    sorter: (a: any, b: any) => realizedPnlWithFeesOf(a) - realizedPnlWithFeesOf(b),
                    render: (text: any, item) => {
                      const EP = EPs?.find(
                        (item2: any) => +item2.id == +item?.exchange_pairs_id
                      );
                      const currency = EP?.pairs?.split('/')[1];


                      const entryFee = item?.orders?.[0]?.fee_price || 0;

                      let exitFees = 0;
                      if (item?.orders && item.orders.length > 1) {
                        for (let i = 1; i < item.orders.length; i++) {
                          if (item.orders?.[i]?.fee_price) {
                            exitFees += Number(item.orders?.[i].fee_price);
                          }
                        }
                      }
                      
                      // 원래 포지션 대비 청산된 비율 계산
                      const originalAmount = item?.orders?.[0]?.amount || 0;
                      const closedAmount = originalAmount - (item?.amount || 0);
                      const closedRatio = originalAmount > 0 ? 
                        BigNumber(closedAmount).dividedBy(originalAmount).toNumber() : 0;
                      
                      // 실현된 진입 수수료 (원래 진입 수수료 * 청산 비율)
                      const realizedEntryFee = BigNumber(entryFee).multipliedBy(closedRatio).toNumber();

                      // console.log('item?.pnl_price', item)

                      let realizedPnLWithFees = 0;

                      if (closedAmount > 0 && item.pnl_price) {
                        realizedPnLWithFees = BigNumber(item.pnl_price)
                          .minus(realizedEntryFee)
                          .minus(exitFees)
                          .toNumber();
                      } else if (closedAmount === 0 && entryFee > 0) {
                        realizedPnLWithFees = -entryFee;
                      }


                      return (
                        <View style={{ minWidth: 90 }}>
                          <KrwPrice
                            price={formatDecimal4Truncate(realizedPnLWithFees)}
                            currency={currency}
                            equiv={equivshow}
                            dp={4}
                          />
                        </View>
                      );
                    },
                  },
                  {
                    width: '9%',
                    title: <div>{t('closeBy')}</div>,
                    dataIndex: 'close_by',
                    key: 'close_by',
                    align: 'left',
                    sorter: false,
                    render: (text: any, item) => {
                      return (
                        <>
                          <Closedby
                            item={item}
                            closeAll={closed}
                            setCloseAll={toggleClosed}
                            refresh={item?.exchange_pairs_id}
                            test={'5'}
                            orderBookValue={orderBookValue}
                          />
                        </>
                      );
                    },
                  },
                  {
                    width: '9%',
                    title: <div>{t('TP/SL')}</div>,
                    dataIndex: 'tp/sl',
                    key: 'tp/sl',
                    align: 'left',
                    sorter: false,
                    render: (text: any, item) => {
                      return (
                        <TPSLEDIT
                          posid={item?.id}
                          tpsl={item?.tpsl?.[0]}
                          price={item?.price}
                          amount={item?.amount}
                          long_short={item?.long_short}
                          leverage={item?.leverage}
                          margin={item?.margin}
                          mark={getPrice(item?.exchange_pairs_id)}
                          liq={item?.liquid_price}
                          exchange_pairs_id={item?.exchange_pairs_id}
                          isMobile={false}
                        />
                      );
                    },
                  },
                ]
            }
            pagination={false}
            style={{ width: '100%', height: '100%' }}
            sticky={true}
            scroll={
              needsScroll ? { x: 'max-content', y: parentHeight - 20 - (headerHeight || 38) } : { x: 'max-content' }  
            }
            size="small"
          />
        </StyledTableContainer>
      ) : (
        <EmptyMessage />
      )}
      <ShareModal shareData={shareData} setShareData={setShareData} />
    </TradeDetailListSection>
  );
};

export default React.memo(TradePositionsTable);

const StyledTableContainer = styled.div`
  width: 100%;
  height: 100%;
  
  .ant-table-wrapper {
    .ant-table {
      .ant-table-container {
        .ant-table-body {
          overflow: auto !important;
          
          /* 스크롤바 커스터마이징 */
          &::-webkit-scrollbar {
            width: 8px;
            height: 8px;
          }
          
          &::-webkit-scrollbar-track {
            background: transparent;
          }
          
          &::-webkit-scrollbar-thumb {
            background: rgba(255, 255, 255, 0.3);
            border-radius: 4px;
            
            &:hover {
              background: rgba(255, 255, 255, 0.5);
            }
          }
          
          /* Firefox 스크롤바 스타일링 */
          scrollbar-width: thin;
          scrollbar-color: rgba(255, 255, 255, 0.3) transparent;
        }
      }
    }
  }
`;

const PlusButton = styled.div`
  font-size: 20px;
  margin-bottom: 6px;
  color: #f87943;
  cursor: pointer;
  &:hover {
    color: #ff9b70;
  }
`;